import React from "react";
import { observer } from "mobx-react";
import { Grid, Box } from "@mui/material";
import { useRootStore } from "../../../../store/contexts";
import { GroupContainer } from "../../../../common/components";
import { FormToolbox } from "../../../../common/controls/forms/FormToolbox";
import { FormSaveFooter } from "../../../../common/controls/forms/FormSaveFooter";

import FormExtras from "../../../../common/components/formExtras";
import { AlertSubscriptionUpdateStore } from "../../store";
import { InputReference } from "../../../common/controls";
import { CreateEntityDialog } from "../../../common/controls";
import { AlertRuleCreate } from "../../../alertRule/components/create/AlertRuleCreate";
import { AlertRuleCreateStore } from "../../../alertRule/store";
import { InputBoolean } from "../../../common/controls";
import { FormControl } from "@mui/material";

export type AlertSubscriptionUpdateFormProps = {
  onSave?: () => void;
  onCancel?: () => void;
  /** Mientras se guarda, Guardar queda deshabilitado. */
  saving?: boolean;
  showToolbox?: boolean;
  showSaveFooter?: boolean;
  alertRuleSelectedValue?: { displayId: string; id: string } | null;
  onAlertRuleChange?: (value: string) => void;
  onAlertRuleSelected?: (
    value: { displayId: string; id: string } | null
  ) => void;
  alertRuleOptions?: { displayId: string; id: string }[];
  alertRuleError?: string;
  enabled: boolean;
  enabledSelectedValue?: { displayId: string; id: string } | null;
  onEnabledChange?: (value: boolean) => void;
  enabledError?: string;
  alertSubscriptionUpdateStore: AlertSubscriptionUpdateStore;
};

export const AlertSubscriptionUpdateForm: React.FC<AlertSubscriptionUpdateFormProps> =
  observer(
    ({
      onSave = () => {},
      onCancel = () => {},
      saving = false,
      showToolbox = true,
      showSaveFooter,
      onAlertRuleSelected = () => {},
      alertRuleOptions = [],
      alertRuleSelectedValue = null,
      alertRuleError,
      onAlertRuleChange = () => {},
      enabled = false,
      enabledError,
      onEnabledChange = () => {},
      alertSubscriptionUpdateStore,
    }) => {
      const rootStore = useRootStore();
      const { langStore } = rootStore;

      return (
        <Grid>
          {showToolbox && (
            <FormToolbox
              saveAndCloseLabel={langStore.saveEntity}
              closeLabel={langStore.cancelUpdatedEntity}
              saveForm={() => onSave()}
              saveDisabled={saving}
              closeForm={() => onCancel()}
            />
          )}
          <Box>
            <FormExtras
              entity="AlertSubscription"
              mode="update"
              values={{}}
              options={{ alertRule: alertRuleOptions }}
              onPatch={(campo: string, valor: any) => {
                switch (campo) {
                  case "alertRule":
                    onAlertRuleSelected?.(valor);
                    break;
                  case "enabled":
                    onEnabledChange?.(valor);
                    break;
                }
              }}
            />
            <GroupContainer
              id="null"
              errorText={alertSubscriptionUpdateStore.getCardErrors("null")}
              title={langStore.groupAlertSubscriptionLabel}
              collapsible={false}
            >
              <FormControl fullWidth>
                <InputReference
                  id="alertRule"
                  label={langStore.AlertSubscriptionAlertRuleLabel}
                  autoFocus
                  error={Boolean(alertRuleError)}
                  helperText={
                    alertRuleError ||
                    alertSubscriptionUpdateStore.warnings["alertRule"]?.message
                  }
                  selectedValue={alertRuleSelectedValue}
                  onChange={(value: string) => onAlertRuleChange(value)}
                  onSelect={(value: { displayId: string; id: string } | null) =>
                    onAlertRuleSelected(value)
                  }
                  options={alertRuleOptions}
                  canCreate={rootStore.authStore.hasPermissions(
                    "alertRule_create"
                  )}
                  renderCreateDialog={(dialogProps) => (
                    <CreateEntityDialog
                      {...dialogProps}
                      createStore={(rootStore) =>
                        new AlertRuleCreateStore(rootStore)
                      }
                      renderForm={(store) => (
                        <AlertRuleCreate
                          alertRuleCreateStore={store}
                          showToolbox={false}
                          showSaveFooter={false}
                        />
                      )}
                      saveMethod="saveNewAlertRule"
                      cancelMethod="cancelNewAlertRule"
                    />
                  )}
                />
                <InputBoolean
                  value={enabled}
                  error={Boolean(enabledError)}
                  helperText={
                    enabledError ||
                    alertSubscriptionUpdateStore.warnings["enabled"]?.message
                  }
                  label={langStore.AlertSubscriptionEnabledLabel}
                  onChange={(value: boolean) => onEnabledChange(value)}
                />
              </FormControl>
            </GroupContainer>
          </Box>
          {(showSaveFooter ?? !showToolbox) && (
            <FormSaveFooter
              onSave={() => onSave()}
              label={langStore.saveEntity}
              disabled={saving}
            />
          )}
        </Grid>
      );
    }
  );
